import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, InputAdornment } from "@mui/material"
import type { FC } from "react"
import { type Resolver, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import {
  type FreightLinePriceFormValues,
  deliveryDateLinePriceOverrideFormSchema,
  freightLinePriceFormSchema,
} from "@/screens/CustomerOrders/schemas"

const FREIGHT_PRICE_DECIMAL_SCALE = 2

interface SalesOrderLinePriceOverrideDialogProps {
  pricePerUom: number | null
  onClose: () => void
  onSubmit: (formValues: FreightLinePriceFormValues) => void
  title?: string
  // When set, the dialog uses freightLinePriceFormSchema (allows zero), shows a currency-formatted
  // field with the unit label and uses the "Change" confirm label.
  isFreightPrice?: boolean
  priceUomId?: string | null
  unitLabel?: string
}

export const SalesOrderLinePriceOverrideDialog: FC<SalesOrderLinePriceOverrideDialogProps> = ({
  pricePerUom,
  onClose,
  onSubmit,
  title = "Override Price",
  isFreightPrice,
  priceUomId = null,
  unitLabel,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, errors, isSubmitting },
  } = useForm<FreightLinePriceFormValues>({
    defaultValues: { pricePerUom, priceUomId },
    resolver: zodResolver(
      isFreightPrice ? freightLinePriceFormSchema : deliveryDateLinePriceOverrideFormSchema
    ) as Resolver<FreightLinePriceFormValues>,
    mode: "onChange",
  })

  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting

  const handleFormSubmit = handleSubmit((formValues) => {
    onSubmit(formValues)
  })

  return (
    <Dialog open onClose={onClose} maxWidth='xs' fullWidth data-testid='customer-orders.so-line-price-override-dialog'>
      <DialogHeader title={title} onClose={onClose} />
      <DialogContent className='flex flex-col gap-4'>
        <FormNumericField
          control={control}
          name='pricePerUom'
          label='Price per UoM'
          required
          decimalScale={isFreightPrice ? FREIGHT_PRICE_DECIMAL_SCALE : undefined}
          fixedDecimalScale={isFreightPrice}
          prefix={isFreightPrice ? "$" : undefined}
          slotProps={
            isFreightPrice && unitLabel
              ? { input: { endAdornment: <InputAdornment position='end'>{unitLabel}</InputAdornment> } }
              : undefined
          }
          data-testid='customer-orders.so-line-price-override-dialog.price-field'
        />
      </DialogContent>
      <DialogActions className='flex justify-between px-6 pb-6'>
        <Button
          appearance='outlined'
          onClick={onClose}
          data-testid='customer-orders.so-line-price-override-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          onClick={handleFormSubmit}
          disabled={!canSubmit}
          data-testid='customer-orders.so-line-price-override-dialog.submit-button'
        >
          {isFreightPrice ? "Change" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
